import { EnvironmentGroupId } from '@/react/portainer/environments/types';

import { Option } from '@@/form-components/PortainerSelect';

export const VULNERABILITY_SCANNERS = ['vulnerability'] as const;
export type VulnerabilityScanner = (typeof VULNERABILITY_SCANNERS)[number];

// Ordered most severe first, matching the API and how the checkboxes render.
export const TRIVY_SEVERITIES = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'UNKNOWN',
] as const;
export type TrivySeverity = (typeof TRIVY_SEVERITIES)[number];

// LOW and UNKNOWN were 76% of findings on a representative cluster and no
// default alert tier covers them, so they are off by default.
export const DEFAULT_SEVERITIES: Array<TrivySeverity> = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
];

export const NAMESPACE_SCOPE_MODES = ['all-except', 'only'] as const;
export type NamespaceScopeMode = (typeof NAMESPACE_SCOPE_MODES)[number];

// The API takes a Go duration string, which tops out at hours — trivy-operator
// parses scannerReportTTL the same way, so "7d" is rejected on-cluster.
export const RESCAN_INTERVAL_UNIT_HOURS = {
  hours: 1,
  days: 24,
  weeks: 168,
} as const;
export type RescanIntervalUnit = keyof typeof RESCAN_INTERVAL_UNIT_HOURS;

export const RESCAN_INTERVAL_UNIT_OPTIONS: ReadonlyArray<
  Option<RescanIntervalUnit>
> = [
  { label: 'hours', value: 'hours' },
  { label: 'days', value: 'days' },
  { label: 'weeks', value: 'weeks' },
];

export const MIN_RESCAN_INTERVAL_HOURS = 1;
export const MIN_CONCURRENT_SCANS = 1;
export const MAX_CONCURRENT_SCANS = 10;

export const DEFAULT_TARGET_NAMESPACE = 'trivy-system';
export const DEFAULT_EXCLUDED_NAMESPACES = [
  'kube-system',
  'kube-public',
  'kube-node-lease',
  'portainer',
  DEFAULT_TARGET_NAMESPACE,
];

export function isNamespaceScopeMode(
  value: unknown
): value is NamespaceScopeMode {
  return NAMESPACE_SCOPE_MODES.includes(value as NamespaceScopeMode);
}

export function isRescanIntervalUnit(
  value: unknown
): value is RescanIntervalUnit {
  return Object.keys(RESCAN_INTERVAL_UNIT_HOURS).includes(value as string);
}

export interface NamespaceScope {
  mode: NamespaceScopeMode;
  namespaces: Array<string>;
}

// The two lists are kept apart so switching mode doesn't carry the excluded
// namespaces over into the include list, and so either survives a mode change.
export interface VulnerabilityScanningFormValues {
  name: string;
  environmentGroupIds: Array<EnvironmentGroupId>;
  rescanIntervalValue: number;
  rescanIntervalUnit: RescanIntervalUnit;
  maxConcurrentScans: number;
  severities: Array<string>;
  targetNamespace: string;
  namespaceScopeMode: NamespaceScopeMode;
  excludedNamespaces: Array<string>;
  includedNamespaces: Array<string>;
}

export interface VulnerabilityScanningPolicyPayload {
  scanners: Array<VulnerabilityScanner>;
  rescanInterval: string;
  maxConcurrentScans: number;
  severities: Array<TrivySeverity>;
  targetNamespace: string;
  namespaceScope: NamespaceScope;
}

export const VULNERABILITY_SCANNING_INITIAL_VALUES: VulnerabilityScanningFormValues =
  {
    name: '',
    environmentGroupIds: [],
    rescanIntervalValue: 7,
    rescanIntervalUnit: 'days',
    maxConcurrentScans: 2,
    severities: DEFAULT_SEVERITIES,
    targetNamespace: DEFAULT_TARGET_NAMESPACE,
    namespaceScopeMode: 'all-except',
    excludedNamespaces: DEFAULT_EXCLUDED_NAMESPACES,
    includedNamespaces: [],
  };

export function isTrivySeverity(value: unknown): value is TrivySeverity {
  return TRIVY_SEVERITIES.includes(value as TrivySeverity);
}

// Orders by severity regardless of click order and drops anything unrecognised.
export function sortSeverities(
  severities: Array<string>
): Array<TrivySeverity> {
  return TRIVY_SEVERITIES.filter((severity) => severities.includes(severity));
}

export function activeNamespaceField(
  mode: NamespaceScopeMode
): 'excludedNamespaces' | 'includedNamespaces' {
  return mode === 'all-except' ? 'excludedNamespaces' : 'includedNamespaces';
}

export function toRescanIntervalHours(
  value: number,
  unit: RescanIntervalUnit
): number {
  return value * RESCAN_INTERVAL_UNIT_HOURS[unit];
}

export function fromRescanIntervalHours(hours: number): {
  rescanIntervalValue: number;
  rescanIntervalUnit: RescanIntervalUnit;
} {
  const units: Array<RescanIntervalUnit> = ['weeks', 'days', 'hours'];
  const unit =
    units.find(
      (candidate) => hours % RESCAN_INTERVAL_UNIT_HOURS[candidate] === 0
    ) ?? 'hours';

  return {
    rescanIntervalValue: hours / RESCAN_INTERVAL_UNIT_HOURS[unit],
    rescanIntervalUnit: unit,
  };
}
